import { Button, Dropdown, DropdownOption } from '@hawkins/components';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import { NumberParam, useQueryParams, withDefault } from 'use-query-params';
import { number, object } from 'yup';

import {
  aspectRatioLookup,
  defaultAspectRatio,
  minAnamorphicSqueezeFactorLookup,
} from 'src/domains/api/static';
import { Grid } from 'src/domains/shared/components/Grid';
import { createEncodedObjectParam } from 'src/domains/shared/hooks/useQueryParams/EncodedObjectParam';

import { ProtectionInput } from '../FramingAndWorkingResolutionCalculators/components/CalculatorInput/ProtectionInput';
import { KEY } from './constants';
import { calculateWorkingResolution } from './process';
import { CalculatorInputProps, FormState } from './types';

import styles from './CalculatorInput.module.css';

const defaultLensSqueezeRatio =
  minAnamorphicSqueezeFactorLookup[minAnamorphicSqueezeFactorLookup.length - 1];

const validationSchema = object({
  macrOutputAspectRatio: object().default(defaultAspectRatio),
  macrLensSqueezeFactor: object().default(defaultLensSqueezeRatio),
  macrSafetyCrop: number().default(0),
}).required();

const initialFormValues: FormState = {
  macrLensSqueezeFactor: defaultLensSqueezeRatio,
  macrOutputAspectRatio: defaultAspectRatio,
  macrSafetyCrop: 0,
};

export const CalculatorInput = ({ onChange }: CalculatorInputProps) => {
  const [queryParams, setQueryParams] = useQueryParams({
    macrOutputAspectRatio: withDefault(
      createEncodedObjectParam<string, DropdownOption<number>>(),
      defaultAspectRatio
    ),
    macrLensSqueezeFactor: withDefault(
      createEncodedObjectParam<string, DropdownOption<number>>(),
      defaultLensSqueezeRatio
    ),
    macrSafetyCrop: withDefault(NumberParam, 0),
  });

  const form = useFormik({
    initialValues: queryParams,
    validationSchema,
    onReset: (values) => {
      setQueryParams(values);
    },
    onSubmit: async (_values, actions) => {
      actions.setSubmitting(false);
    },
  });

  useEffectOnce(() => {
    if (typeof window !== 'undefined') {
      import('@netflix/nfa-logger/lib/helper').then(({ nfa }) => {
        nfa.logFeatureEngaged('calculators', KEY);
      });
    }
  });

  useEffect(() => {
    const safetyCrop = form.values.macrSafetyCrop;
    const outputAspectRatio = form.values.macrOutputAspectRatio.value;
    const lensSqueezeFactor = form.values.macrLensSqueezeFactor.value;

    const output = calculateWorkingResolution({
      safetyCrop,
      outputAspectRatio,
      lensSqueezeFactor,
    });

    onChange(KEY, form.values, output);

    setQueryParams(form.values);
  }, [form.values, onChange, setQueryParams]);

  return (
    <form onSubmit={form.handleSubmit}>
      <Grid gap="Space16">
        <Grid gap="Space8" columns="minmax(0, 500px)">
          <Dropdown
            searchable
            dropdownWidthType="full"
            label="Lens Squeeze Factor"
            multi={false}
            options={minAnamorphicSqueezeFactorLookup}
            value={form.values.macrLensSqueezeFactor}
            onChange={(selected: FormState['macrLensSqueezeFactor'] | null) => {
              form.setFieldValue('macrLensSqueezeFactor', selected);
            }}
          />

          <Dropdown
            searchable
            dropdownWidthType="full"
            label="Output Aspect Ratio"
            multi={false}
            options={aspectRatioLookup}
            value={form.values.macrOutputAspectRatio}
            onChange={(selected: FormState['macrOutputAspectRatio'] | null) => {
              form.setFieldValue('macrOutputAspectRatio', selected);
            }}
          />

          <ProtectionInput
            value={form.values.macrSafetyCrop}
            onChange={(value) => {
              form.setFieldValue('macrSafetyCrop', value);
            }}
          />
        </Grid>

        <Button
          onClick={() =>
            form.resetForm({
              values: initialFormValues,
            })
          }
          variant="secondary-destructive"
          className={styles.macrResetButton}
        >
          Reset
        </Button>
      </Grid>
    </form>
  );
};
